import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ChartProvider from '../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../charts/echarts/ColorProvider'
import Tooltip from '../../../charts/echarts/Tooltip'
import ComponentTreeMap from '../../../charts/echarts/ComponentTreeMap'
import { useFormattingContext } from '../../../organisms/FormattingProvider/FormattingContext'
import { buildLegendConfig, buildLabelConfig, EMPTY_OBJ } from './chartLayoutUtils'

/**
 * Recursively groups flat data into a hierarchical tree.
 * Mirrors the algorithm in DDPTreeMap.
 */
function groupData (data, indexBy, valueAccessor) {
  if (!Array.isArray(data) || !Array.isArray(indexBy) || indexBy.length === 0) {
    return []
  }

  const [currentKey, ...restKeys] = indexBy

  const groups = data.reduce((acc, item) => {
    const key = String(get(item, currentKey) ?? 'Unknown')
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  return Object.keys(groups).map(key => {
    const items = groups[key]

    if (restKeys.length === 0) {
      const value = items.reduce((sum, item) => sum + (Number(get(item, valueAccessor)) || 0), 0)
      return { name: key, value }
    }

    const children = groupData(items, restKeys, valueAccessor)
    const value = children.reduce((acc, c) => acc + (c.value || 0), 0)
    return { name: key, value, children }
  })
}

// TODO: colorAccessor is collected by the admin editor and stored in the config,
// but not yet wired here. Needs ECharts visualMap integration to map a column's
// values to node colors. For now, ECharts uses the default color palette.
function TreeMapLayout ({ config, data, styleOverrides }) {
  const {
    height = 400,
    colorTheme = 'primary',
    indexBy,
    valueAccessor,
    rootLabel = 'All',
    tooltip = {},
    display = {},
    overrides = null,
    seriesOverrides = null
  } = config

  const { label: displayLabel = EMPTY_OBJ, legend: displayLegend = EMPTY_OBJ, grid: displayGrid = EMPTY_OBJ } = display

  const legendConfig = buildLegendConfig(displayLegend)

  const { formatter: fmtFn } = useFormattingContext()
  const labelConfig = useMemo(() => {
    const base = buildLabelConfig(displayLabel)
    if (displayLabel.format) {
      return { ...base, formatter: (params) => fmtFn(params.value, displayLabel.format) }
    }
    return base
  }, [displayLabel, fmtFn])

  const treeData = useMemo(() => {
    if (!Array.isArray(data) || !indexBy || !valueAccessor) return []
    return groupData(data, indexBy, valueAccessor)
  }, [data, indexBy, valueAccessor])

  return (
    <ChartColorProvider theme={colorTheme}>
      <ChartProvider height={height} loadingTemplate='pie' {...(legendConfig && { legend: legendConfig })} {...(overrides || {})}>
        <Tooltip
          trigger={tooltip.trigger || 'item'}
          {...(tooltip.valueFormat && { valueFormat: tooltip.valueFormat })}
        />
        <ComponentTreeMap
          seriesId='treemap'
          name={rootLabel}
          data={treeData}
          left={displayGrid.left ?? '10%'}
          top={displayGrid.top ?? '10%'}
          right={displayGrid.right ?? '10%'}
          bottom={displayGrid.bottom ?? '10%'}
          label={labelConfig}
          {...(styleOverrides?.styles?.borderWidth != null && { borderWidth: styleOverrides.styles.borderWidth })}
          {...(styleOverrides?.styles?.gapWidth != null && { gapWidth: styleOverrides.styles.gapWidth })}
          {...(styleOverrides?.props ?? {})}
          {...(seriesOverrides || {})}
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

TreeMapLayout.propTypes = {
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  styleOverrides: PropTypes.object
}

export default TreeMapLayout
